import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "./CardOtpForm.css";
import { submitCardPin_Otp } from "../../../redux/createOrderAction";
import PinOtpSteps from "./PinOtpSteps";
import SecuredBadge from "../SecuredBadge";
import {
  readCardAuthContext,
  CARD_AUTH_TTL_MS,
} from "../../../utils/cardPayment";
import { goToSignIn } from "../../../utils/signInReturn";

/**
 * /v3/validate-charge — card OTP entry (2026-07 redesign).
 *
 * Masked-by-default OTP with a show/hide toggle, payment context (amount +
 * last4), the bank's "OTP sent to…" hint from the server response, the same
 * 15-min TTL countdown as the PIN step, and real failure reasons. OTP length
 * is flexible (4–8) because issuing banks vary; the old form hard-required 5.
 * Dispatch contract (submitCardPin_Otp + client_id fallback) unchanged.
 */
const CardOtpForm = () => {
  const user = useSelector((state) => state.user.currentUser);
  const { paymentCreationStatus, order, error } = useSelector(
    (state) => state.orders
  );
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const ctx = useMemo(() => readCardAuthContext(), []);

  const [remainingMs, setRemainingMs] = useState(() =>
    ctx?.startedAt ? Math.max(0, ctx.startedAt + CARD_AUTH_TTL_MS - Date.now()) : null
  );
  useEffect(() => {
    if (!ctx?.startedAt) return undefined;
    const t = setInterval(() => {
      setRemainingMs(Math.max(0, ctx.startedAt + CARD_AUTH_TTL_MS - Date.now()));
    }, 1000);
    return () => clearInterval(t);
  }, [ctx]);
  const expired = remainingMs !== null && remainingMs <= 0;

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (!user) goToSignIn(history);
  }, [user, history]);

  useEffect(() => {
    if (paymentCreationStatus === "fulfilled" && order?.redirectUrl) {
      history.push(order.redirectUrl);
    }
  }, [paymentCreationStatus, order, history]);

  const pending = paymentCreationStatus === "pending";
  const rejected = paymentCreationStatus === "rejected";
  // The PIN step's response carries the bank's hint (e.g. "OTP sent to 25670…").
  const bankHint =
    typeof order?.message === "string" && /otp/i.test(order.message)
      ? order.message
      : null;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (otp.length < 4 || pending || expired) return;
    dispatch(
      submitCardPin_Otp({
        cardOtp: otp,
        client_id: user?.result?._id || user?._id,
      })
    );
  };

  const mmss = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  return (
    <div className="pa-wrap">
      <div className="pa-card">
        <PinOtpSteps step1Completed step2 />
        <h2 className="pa-title">Enter the one-time password</h2>

        {(ctx?.amount || ctx?.last4) && (
          <div className="pa-context">
            {ctx?.amount && (
              <span className="pa-amount">
                UGX {Number(ctx.amount).toLocaleString()}
              </span>
            )}
            {ctx?.last4 && (
              <span className="pa-cardref">
                {ctx.brand || "Card"} •••• {ctx.last4}
              </span>
            )}
          </div>
        )}

        <p className="pa-hint">
          {bankHint ||
            "Your bank has sent a one-time password to your registered phone or email."}
        </p>

        {remainingMs !== null && !expired && (
          <div className={`pa-timer ${remainingMs < 120000 ? "pa-timer-low" : ""}`}>
            Session expires in {mmss(remainingMs)}
          </div>
        )}

        {expired ? (
          <div className="pa-expired">
            <p>
              This payment session has expired (15&nbsp;minutes). <b>No charge was
              made.</b> Please start the payment again.
            </p>
            <button type="button" className="pa-btn" onClick={() => history.push("/")}>
              Back to Lusuku
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="pa-label" htmlFor="card-otp">
              One-time password
            </label>
            <div className="pa-otp-row">
              <input
                id="card-otp"
                className="pa-otp-input"
                type={showOtp ? "text" : "password"}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                value={otp}
                disabled={pending}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                aria-label="One-time password"
              />
              <button
                type="button"
                className="pa-toggle"
                onClick={() => setShowOtp((v) => !v)}
                aria-label={showOtp ? "Hide OTP" : "Show OTP"}
              >
                {showOtp ? "Hide" : "Show"}
              </button>
            </div>

            {rejected && (
              <div className="pa-error" role="alert">
                {typeof error === "string" && error
                  ? error
                  : "The one-time password could not be verified. Check it and try again."}
              </div>
            )}

            <button
              type="submit"
              className="pa-btn"
              disabled={otp.length < 4 || pending}
            >
              {pending ? "Verifying…" : rejected ? "Try again" : "Confirm payment"}
            </button>
          </form>
        )}

        <SecuredBadge />
      </div>
    </div>
  );
};

export default CardOtpForm;
